import React from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Image,
  useColorScheme,
  StyleSheet,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const SocialHomeScreen = ({ navigation }) => {
  const isDarkMode = useColorScheme() === 'dark';

  // 模拟最近活动数据
  const recentActivities = [
    {
      id: '1',
      userName: '李明',
      avatar: 'https://via.placeholder.com/50',
      action: '完成了"连续健走7天"挑战',
      timeAgo: '2小时前',
    },
    {
      id: '2',
      userName: '王芳',
      avatar: 'https://via.placeholder.com/50',
      action: '分享了一篇健康食谱《低脂高蛋白早餐》',
      timeAgo: '5小时前',
    },
    {
      id: '3',
      userName: '张伟',
      avatar: 'https://via.placeholder.com/50',
      action: '在减重挑战中取得了进步',
      timeAgo: '昨天',
    },
  ];

  // 社交功能列表
  const socialFeatures = [
    {
      id: 'community',
      title: '社区动态',
      icon: 'people',
      description: '了解好友最新健康动态',
      screen: 'CommunityFeed',
    },
    {
      id: 'friends',
      title: '好友互动',
      icon: 'person-add',
      description: '查找并添加志同道合的朋友',
      screen: 'FriendList',
    },
    {
      id: 'comparison',
      title: '数据对比',
      icon: 'analytics',
      description: '与好友比较健康数据',
      screen: 'FriendComparison',
    },
    {
      id: 'challenges',
      title: '健康挑战',
      icon: 'trophy',
      description: '参与挑战，赢取成就',
      screen: 'Challenges',
    },
    {
      id: 'rankings',
      title: '健康排行榜',
      icon: 'podium',
      description: '查看你在好友圈中的排名',
      screen: 'Rankings',
    },
    {
      id: 'sharing',
      title: '经验分享',
      icon: 'book',
      description: '分享你的健康食谱和经验',
      screen: 'ExperienceSharing',
    },
    {
      id: 'groups',
      title: '互助小组',
      icon: 'people-circle',
      description: '加入志同道合的健康小组',
      screen: 'SupportGroup',
    },
    {
      id: 'achievements',
      title: '成就系统',
      icon: 'ribbon',
      description: '解锁健康成就与徽章',
      screen: 'Achievements',
    },
  ];

  // 处理功能卡片点击
  const handleFeaturePress = (feature) => {
    console.log(`Navigating to ${feature.screen}`);
    navigation.navigate(feature.screen);
  };

  return (
    <ScrollView 
      style={[
        styles.container,
        isDarkMode ? styles.containerDark : styles.containerLight
      ]}
    >
      {/* 欢迎区域 */}
      <View style={styles.welcomeArea}>
        <Text style={[styles.welcomeTitle, isDarkMode && styles.textLight]}>
          社交健康
        </Text>
        <Text style={[styles.welcomeSubtitle, isDarkMode && styles.textLight]}>
          与朋友一起享受健康生活
        </Text>
      </View>

      {/* 最近活动 */}
      <View style={styles.sectionContainer}>
        <Text style={[styles.sectionTitle, isDarkMode && styles.textLight]}>
          最近活动
        </Text>
        {recentActivities.map(activity => (
          <View 
            key={activity.id} 
            style={[
              styles.activityCard,
              isDarkMode ? styles.cardDark : styles.cardLight
            ]}
          >
            <Image 
              source={{ uri: activity.avatar }} 
              style={styles.avatar} 
            />
            <View style={styles.activityContent}>
              <Text style={[styles.activityText, isDarkMode && styles.textLight]}>
                <Text style={styles.userName}>{activity.userName}</Text> {activity.action}
              </Text>
              <Text style={[styles.timeAgo, isDarkMode && styles.textLightSecondary]}>
                {activity.timeAgo}
              </Text>
            </View>
          </View>
        ))}
      </View>

      {/* 社交功能 */}
      <View style={styles.featuresContainer}>
        <Text style={[styles.sectionTitle, isDarkMode && styles.textLight]}>
          社交功能
        </Text>
        <View style={styles.featuresGrid}>
          {socialFeatures.map(feature => (
            <TouchableOpacity 
              key={feature.id}
              style={[
                styles.featureCard,
                isDarkMode ? styles.cardDark : styles.cardLight
              ]}
              onPress={() => handleFeaturePress(feature)}
            >
              <View 
                style={[
                  styles.iconContainer,
                  { backgroundColor: isDarkMode ? '#333' : '#e6efff' }
                ]}
              >
                <Ionicons 
                  name={feature.icon} 
                  size={24} 
                  color={isDarkMode ? '#4CAF50' : '#2563eb'} 
                />
              </View>
              <Text style={[styles.featureTitle, isDarkMode && styles.textLight]}>
                {feature.title}
              </Text>
              <Text 
                style={[styles.featureDescription, isDarkMode && styles.textLightSecondary]}
                numberOfLines={2}
              >
                {feature.description}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  containerLight: {
    backgroundColor: '#f5f5f5',
  },
  containerDark: {
    backgroundColor: '#121212',
  },
  textLight: {
    color: '#f5f5f5',
  },
  textLightSecondary: {
    color: '#aaa',
  },
  welcomeArea: {
    marginTop: 10,
    marginBottom: 24,
  },
  welcomeTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  welcomeSubtitle: {
    fontSize: 16,
    color: '#666',
  },
  sectionContainer: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  activityCard: {
    flexDirection: 'row',
    padding: 12,
    borderRadius: 12,
    marginBottom: 10,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 1,
  },
  cardLight: {
    backgroundColor: '#fff',
  },
  cardDark: {
    backgroundColor: '#2a2a2a',
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
  },
  activityContent: {
    flex: 1,
  },
  activityText: {
    fontSize: 14,
    color: '#333',
    lineHeight: 20,
  },
  userName: {
    fontWeight: 'bold',
  },
  timeAgo: {
    fontSize: 12,
    color: '#888',
    marginTop: 4,
  },
  featuresContainer: {
    marginBottom: 24,
  },
  featuresGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  featureCard: {
    width: '48%',
    padding: 16,
    borderRadius: 12,
    marginBottom: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  featureTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 6,
  },
  featureDescription: {
    fontSize: 12,
    color: '#666',
    lineHeight: 16,
  },
});

export default SocialHomeScreen; 